import { AttachmentOffice } from './AttachmentOffice';
import { DictionaryInterface } from 'titus-ts/dist/js/Common/Util/DictionaryInterface';
import { Recipient, EmailUser } from 'titus-ts/dist/js/Mail/Recipient';
import { AddinApi } from './AddinApi';
import { OutlookExchangeOnPrem } from './OutlookApi-ExchangeOnPrem';
import { OutlookExhangeOnline } from './OutlookApi-ExchangeOnline';

export interface IExchangeRequestGenerator {
    getInternetHeaders(itemId: string, headers: string[]): Promise<Record<string, string>>;
    getInReplyToByItemId(itemId: string): Promise<string>;
    getOriginalItemInternetHeaders(
        originalItemInternetId: string,
        headersToRequest: string[],
        itemId?: string,
    ): Promise<Record<string, string>>;
    getOriginalItemSender(originalItemInternetId: string): Promise<EmailUser>;
    getAttachments(itemId: string): Promise<AttachmentOffice[]>;
    setInternetHeaders(itemId: string, headers: DictionaryInterface<string>): Promise<void>;
}

export class OutlookApi extends AddinApi {
    public exchangeRequestHandler: IExchangeRequestGenerator;

    constructor() {
        super();
        this.getRecipientsPromise.bind(this);
        this.getToAsync = this.getToAsync.bind(this);
        this.setToAsync = this.setToAsync.bind(this);
        this.addToAsync = this.addToAsync.bind(this);
        this.addCcAsync = this.addCcAsync.bind(this);
        this.getCcAsync = this.getCcAsync.bind(this);
        this.setCcAsync = this.setCcAsync.bind(this);
        this.getBccAsync = this.getBccAsync.bind(this);
        this.setBccAsync = this.setBccAsync.bind(this);
        this.addBccAsync = this.addBccAsync.bind(this);
        this.exchangeRequestHandler = new OutlookExhangeOnline();
    }

    public initialise() {
        if (this.isSupportedOnPremExchange()) {
            this.exchangeRequestHandler = new OutlookExchangeOnPrem();
        }
    }

    public sendEwsRequestAsync = (envelope: string): Promise<Office.AsyncResult<string>> => {
        return new Promise<Office.AsyncResult<string>>((resolve, reject) => {
            Office.context.mailbox.makeEwsRequestAsync(envelope, (result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve(result);
                } else {
                    reject(result.error.message);
                }
            });
        });
    };

    public saveAsync = (): Promise<string> => {
        return new Promise((resolve, reject) => {
            if (Office.context.mailbox.item) {
                Office.context.mailbox.item.saveAsync((result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(result.value);
                    } else {
                        reject(result.error.message);
                    }
                });
            } else {
                reject('Item is undefined');
            }
        });
    };
    public getAttachmentsAsync = (itemId: string): Promise<AttachmentOffice[]> => {
        return this.exchangeRequestHandler.getAttachments(itemId);
    };

    public setInternetHeaders = (itemId: string, headers: DictionaryInterface<string>): Promise<void> => {
        if (Office.context.mailbox.item?.itemType === Office.MailboxEnums.ItemType.Appointment) {
            return this.setCustomProperties(headers);
        } else {
            return this.exchangeRequestHandler.setInternetHeaders(itemId, headers);
        }
    };
    public insertPortionMark(portionMarkStartText: string, portionMarkEndText: string) {
        try {
            // Check if the API is supported in the current context.
            if (Office.context.mailbox.item) {
                const item = Office.context.mailbox.item;

                item.getSelectedDataAsync(Office.CoercionType.Html, (asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                        const selectedText = asyncResult.value.data;
                        const newText = `${portionMarkStartText}${selectedText}${portionMarkEndText}`;

                        // Set the modified text back to the selection.
                        item.body.setSelectedDataAsync(
                            newText,
                            { coercionType: Office.CoercionType.Html },
                            (setResult) => {
                                if (setResult.status === Office.AsyncResultStatus.Succeeded) {
                                    console.log(
                                        'Text inserted successfully at the beginning and end of the selection.',
                                    );
                                } else {
                                    console.error('Error inserting text:', setResult.error);
                                }
                            },
                        );
                    } else {
                        console.error('Error getting selected text:', asyncResult.error);
                    }
                });
            } else {
                console.error('The Document API is not supported in this context.');
            }
        } catch (error) {
            console.error('Error:', error);
        }
    }
    public getOriginalItemSender = (inReplyToId: string): Promise<EmailUser> => {
        if (inReplyToId) {
            return this.exchangeRequestHandler.getOriginalItemSender(inReplyToId);
        } else {
            return Promise.resolve(new EmailUser('', ''));
        }
    };

    public setCustomProperties = (propertyNames: DictionaryInterface<string>): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            if (Office.context.mailbox.item) {
                Office.context.mailbox.item.loadCustomPropertiesAsync((asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                        const customProps = asyncResult.value;
                        for (const property in propertyNames) {
                            if (propertyNames.hasOwnProperty(property)) {
                                customProps.set(property, propertyNames[property]);
                            }
                        }
                        customProps.saveAsync((saveResult) => {
                            if (saveResult.status === Office.AsyncResultStatus.Succeeded) {
                                resolve();
                            } else {
                                reject(saveResult.error.message);
                            }
                        });
                    } else {
                        reject(asyncResult.error.message);
                    }
                });
            } else {
                reject('Item is undefined');
            }
        });
    };

    public removeCustomProperties = (propertyNames: DictionaryInterface<string>): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            if (Office.context.mailbox.item) {
                Office.context.mailbox.item.loadCustomPropertiesAsync((asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                        const customProps = asyncResult.value;
                        for (const property in propertyNames) {
                            if (propertyNames.hasOwnProperty(property)) {
                                customProps.remove(property);
                            }
                        }
                        customProps.saveAsync((saveResult) => {
                            if (saveResult.status === Office.AsyncResultStatus.Succeeded) {
                                resolve();
                            } else {
                                reject(saveResult.error.message);
                            }
                        });
                    } else {
                        reject(asyncResult.error.message);
                    }
                });
            } else {
                reject('Item is undefined');
            }
        });
    };

    public getCustomProperties = (propertyNames: string[]): Promise<Record<string, string>> => {
        return new Promise<Record<string, string>>((resolve, reject) => {
            if (Office.context.mailbox.item) {
                Office.context.mailbox.item.loadCustomPropertiesAsync((asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                        const customProps = asyncResult.value;
                        const result: Record<string, string> = {};
                        propertyNames.forEach((propertyName) => {
                            const value = customProps.get(propertyName);
                            if (value !== undefined && value !== null) {
                                result[propertyName] = value;
                            }
                        });
                        resolve(result);
                    } else {
                        reject(asyncResult.error.message);
                    }
                });
            } else {
                reject('Item is undefined');
            }
        });
    };

    public getInternetHeaders = (itemId: string, headers: string[]): Promise<Record<string, string>> => {
        if (Office.context.mailbox.item?.itemType === Office.MailboxEnums.ItemType.Appointment) {
            return this.getCustomProperties(headers);
        } else {
            return this.exchangeRequestHandler.getInternetHeaders(itemId, headers);
        }
    };

    public getOriginalItemInternetHeaders = (
        inReplyToInternetId: string,
        itemId: string,
        headers: string[],
    ): Promise<Record<string, string>> => {
        return this.exchangeRequestHandler.getOriginalItemInternetHeaders(inReplyToInternetId, headers, itemId);
    };

    public getToAsync = (): Promise<Recipient[]> => {
        if (Office.context.mailbox.item) {
            if (Office.context.mailbox.item.itemType === Office.MailboxEnums.ItemType.Appointment) {
                return this.getRecipientsPromise(Office.context.mailbox.item.requiredAttendees, 'TO');
            } else {
                return this.getRecipientsPromise(Office.context.mailbox.item.to, 'TO');
            }
        } else {
            return Promise.reject('Items is undefined.');
        }
    };

    public getCcAsync = (): Promise<Recipient[]> => {
        if (Office.context.mailbox.item) {
            if (Office.context.mailbox.item.itemType === Office.MailboxEnums.ItemType.Appointment) {
                return this.getRecipientsPromise(Office.context.mailbox.item.optionalAttendees, 'CC');
            } else {
                return this.getRecipientsPromise(Office.context.mailbox.item.cc, 'CC');
            }
        } else {
            return Promise.reject('Items is undefined.');
        }
    };

    public getBccAsync = (): Promise<Recipient[]> => {
        if (Office.context.mailbox.item) {
            if (Office.context.mailbox.item.itemType === Office.MailboxEnums.ItemType.Appointment) {
                return Promise.resolve([]);
            } else {
                return this.getRecipientsPromise(Office.context.mailbox.item.bcc, 'BCC');
            }
        } else {
            return Promise.reject('Items is undefined.');
        }
    };

    public setToAsync = (emails: string[]): Promise<void> => {
        if (Office.context.mailbox.item) {
            if (Office.context.mailbox.item.itemType === Office.MailboxEnums.ItemType.Appointment) {
                return this.setRecipientsPromise(Office.context.mailbox.item.requiredAttendees, emails);
            } else {
                return this.setRecipientsPromise(Office.context.mailbox.item.to, emails);
            }
        } else {
            return Promise.reject('Items is undefined.');
        }
    };

    public setCcAsync = (emails: string[]): Promise<void> => {
        if (Office.context.mailbox.item) {
            if (Office.context.mailbox.item.itemType === Office.MailboxEnums.ItemType.Appointment) {
                return this.setRecipientsPromise(Office.context.mailbox.item.optionalAttendees, emails);
            } else {
                return this.setRecipientsPromise(Office.context.mailbox.item.cc, emails);
            }
        } else {
            return Promise.reject('Items is undefined.');
        }
    };

    public setBccAsync = (emails: string[]): Promise<void> => {
        if (Office.context.mailbox.item) {
            if (Office.context.mailbox.item.itemType === Office.MailboxEnums.ItemType.Appointment) {
                return Promise.reject('BCC is not supported for appointments.');
            } else {
                return this.setRecipientsPromise(Office.context.mailbox.item.bcc, emails);
            }
        } else {
            return Promise.reject('Items is undefined.');
        }
    };

    public addToAsync = (emails: string[]): Promise<void> => {
        if (Office.context.mailbox.item) {
            if (Office.context.mailbox.item.itemType === Office.MailboxEnums.ItemType.Appointment) {
                return this.addRecipientsPromise(Office.context.mailbox.item.requiredAttendees, emails);
            } else {
                return this.addRecipientsPromise(Office.context.mailbox.item.to, emails);
            }
        } else {
            return Promise.reject('Items is undefined.');
        }
    };

    public addCcAsync = (emails: string[]): Promise<void> => {
        if (Office.context.mailbox.item) {
            if (Office.context.mailbox.item.itemType === Office.MailboxEnums.ItemType.Appointment) {
                return this.addRecipientsPromise(Office.context.mailbox.item.optionalAttendees, emails);
            } else {
                return this.addRecipientsPromise(Office.context.mailbox.item.cc, emails);
            }
        } else {
            return Promise.reject('Items is undefined.');
        }
    };

    public addBccAsync = (emails: string[]): Promise<void> => {
        if (Office.context.mailbox.item) {
            if (Office.context.mailbox.item.itemType === Office.MailboxEnums.ItemType.Appointment) {
                return Promise.reject('BCC is not supported for appointments.');
            } else {
                return this.addRecipientsPromise(Office.context.mailbox.item.bcc, emails);
            }
        } else {
            return Promise.reject('Items is undefined.');
        }
    };

    public isMailboxVersionSupported = (version: number): boolean => {
        return Office.context.requirements.isSetSupported('MailBox', version);
    };

    public getItemId = (): string => {
        if (Office.context.mailbox.item) {
            return Office.context.mailbox.item.itemId;
        } else {
            return '';
        }
    };

    public getItemType = (): string => {
        const itemType = Office.context.mailbox.item?.itemType?.toString().toLowerCase();
        if (itemType === 'message' || itemType === 'appointment') {
            return itemType;
        }

        return 'unknown';
    };

    public getConversationId = (): string => {
        if (Office.context.mailbox.item) {
            return Office.context.mailbox.item.conversationId;
        } else {
            return '';
        }
    };
    public getInReplyTo = (): string => {
        if (Office.context.mailbox.item) {
            return Office.context.mailbox.item.inReplyTo;
        } else {
            return '';
        }
    };

    public getInReplyToAsync = async (): Promise<string> => {
        const officeInReplyTo = this.getInReplyTo();
        if (officeInReplyTo) {
            return officeInReplyTo;
        }

        if (!this.isSupportedOnPremExchange()) {
            return '';
        }

        try {
            let itemId = this.getItemId();
            if (!itemId) {
                itemId = await this.saveAsync();
            }

            if (!itemId) {
                return '';
            }

            return await this.exchangeRequestHandler.getInReplyToByItemId(itemId);
        } catch {
            return '';
        }
    };

    public getBodyAsTextAsync = (): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            const onItemBodyRetrieved = (result: Office.AsyncResult<string>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve(result.value);
                } else {
                    reject(`Failed getting message body as text... Error: ${result.error}`);
                }
            };

            Office.context.mailbox.item?.body.getAsync(Office.CoercionType.Text, onItemBodyRetrieved);
        });
    };

    public getBodyAsHtmlAsync = (): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            const onItemBodyRetrieved = (result: Office.AsyncResult<string>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve(result.value);
                } else {
                    reject(`Failed getting message body as html... Error: ${result.error}`);
                }
            };

            Office.context.mailbox.item?.body.getAsync(Office.CoercionType.Html, onItemBodyRetrieved);
        });
    };

    public getBodyTypeAsync = (): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            const onBodyTypeRetrieved = (result: Office.AsyncResult<Office.CoercionType>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve(result.value.toString());
                } else {
                    reject(`Failed getting message body as html... Error: ${result.error}`);
                }
            };

            Office.context.mailbox.item?.body.getTypeAsync({}, onBodyTypeRetrieved);
        });
    };

    public setBodyAsync = (newBody: string, bodyType: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            const onSetBodyCallback = (result: Office.AsyncResult<void>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve();
                } else {
                    reject(`Failed setting message body... Error: ${result.error}`);
                }
            };

            const type = bodyType === 'text' ? Office.CoercionType.Text : Office.CoercionType.Html;

            Office.context.mailbox.item?.body.setAsync(newBody, { coercionType: type }, onSetBodyCallback);
        });
    };

    public getSubjectAsync = (): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
            const onItemSubjectRetrieved = (result: Office.AsyncResult<string>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve(result.value);
                } else {
                    reject(`Failed getting message subject... Error: ${result.error}`);
                }
            };

            Office.context.mailbox.item?.subject.getAsync(onItemSubjectRetrieved);
        });
    };

    public setSubjectAsync = (newSubject: string): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            const onSetSubjectCallback = (result: Office.AsyncResult<void>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve();
                } else {
                    reject(`Failed setting message subject... Error: ${result.error}`);
                }
            };

            Office.context.mailbox.item?.subject.setAsync(newSubject, onSetSubjectCallback);
        });
    };

    private getRecipientsPromise = (recipients: Office.Recipients, recipientType: string): Promise<Recipient[]> => {
        return new Promise<Recipient[]>((resolve, reject) => {
            recipients.getAsync({}, (result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    const emails = this.mapEmailAddresses(result.value, recipientType);

                    resolve(emails);
                } else {
                    reject('Error getting recipients. Error: ' + result.error.message);
                }
            });
        });
    };

    private setRecipientsPromise = (recipients: Office.Recipients, emails: string[]): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            recipients.setAsync(emails, {}, (result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve();
                } else {
                    reject('Error setting recipients. Error: ' + result.error.message);
                }
            });
        });
    };

    private addRecipientsPromise = (recipients: Office.Recipients, emails: string[]): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            recipients.addAsync(emails, {}, (result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    resolve();
                } else {
                    reject('Error adding recipients. Error: ' + result.error.message);
                }
            });
        });
    };

    private mapEmailAddresses = (emails: Office.EmailAddressDetails[], recipientType: string): Recipient[] => {
        const result: Recipient[] = [];

        if (emails) {
            for (let index = 0; index < emails.length; index++) {
                const elem = emails[index];
                const recipient = new Recipient(elem.emailAddress, elem.displayName, recipientType);
                recipient.IsDistributionList =
                    elem.recipientType === Office.MailboxEnums.RecipientType.DistributionList;
                result.push(recipient);
            }
        }

        return result;
    };

    private isSupportedOnPremExchange = (): boolean => {
        if (Office?.context?.mailbox) {
            return Office.context.mailbox.userProfile.accountType === 'enterprise';
        }
        return false;
    };

    public getPlatform = (): string => {
        return Office.context.mailbox.diagnostics.hostName;
    };

    public getSenderAddress = (): string => {
        return Office.context.mailbox.userProfile.emailAddress;
    };

    public getSenderDisplayName = (): string => {
        return Office.context.mailbox.userProfile.displayName;
    };

    public messageParent = (message: string): void => {
        Office.context.ui.messageParent(message);
    };

    public onItemChanged = (callback: () => void): void => {
        Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged);
        Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, callback);
    };

    public isMailBoxItemTypeSupported = (): boolean => {
        return (
            (Office.context.mailbox.item?.itemType === Office.MailboxEnums.ItemType.Message ||
                Office.context.mailbox.item?.itemType === Office.MailboxEnums.ItemType.Appointment) ??
            false
        );
    };
}
